<template>
    <div class="credit-note-pennylane-sync">
        <!-- Message si l'avoir ne peut pas être synchronisé -->
        <div v-if="!canBeSynced" class="alert alert-warning mb-0">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Synchronisation non disponible</strong>
            <p class="mb-0 mt-2">Cet avoir ne peut pas être synchronisé (avoir en brouillon).</p>
        </div>

        <div v-else>
            <!-- Statut de synchronisation -->
                <div v-if="!creditNote.pennylane_id" class="alert alert-info mb-3">
                    <i class="fas fa-info-circle mr-2"></i>
                    <strong>Avoir non synchronisé</strong>
                    <p class="mb-0 mt-1 small">Cet avoir n'a pas encore été synchronisé.</p>
                </div>

                <div v-else class="sync-status mb-3 p-3 bg-light rounded">
                    <div class="status-item">
                        <i class="fas fa-check-circle text-success mr-2"></i>
                        <span class="font-weight-bold">Avoir e-synchronisé</span>
                        <small class="d-block text-muted ml-4 mt-1">ID : {{ creditNote.pennylane_id }}</small>
                    </div>
                    <div v-if="creditNote.pennylane_status" class="status-item mt-2 pt-2 border-top">
                        <i class="fas mr-2" :class="creditNote.is_pennylane_awaiting_transmission ? 'fa-exclamation-triangle text-warning' : getStatusIcon(creditNote.pennylane_status)"></i>
                        <span>Statut : <strong>{{ creditNote.is_pennylane_awaiting_transmission ? 'Non transmis' : getStatusLabel(creditNote.pennylane_status) }}</strong></span>
                    </div>
                    <div v-if="creditNote.pennylane_synced_at" class="status-item mt-2 pt-2 border-top">
                        <i class="fas fa-clock text-muted mr-2"></i>
                        <span class="text-muted">Dernière synchronisation : {{ formatSyncDate(creditNote.pennylane_synced_at) }}</span>
                    </div>
                </div>

                <!-- Avertissement si avoir finalisé -->
                <div v-if="creditNote.is_pennylane_finalized" class="alert alert-info mb-3">
                    <i class="fas fa-lock mr-2"></i>
                    <strong>Avoir e-transmis</strong>
                    <p class="mb-0 mt-1 small">
                        Cet avoir est e-transmis et ne peut plus être modifié.
                    </p>
                </div>

                <!-- Importé chez Pennylane mais pas transmis à la plateforme -->
                <div v-else-if="creditNote.is_pennylane_awaiting_transmission" class="alert alert-warning mb-3">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Avoir non transmis</strong>
                    <p class="mb-0 mt-1 small">
                        Il est dans Pennylane mais n'a pas été envoyé à la plateforme (motif dans l'historique).
                        Si le Factur-X est refusé : corrigez, archivez l'avoir dans Pennylane, puis synchronisez.
                    </p>
                </div>

                <!-- Avertissement de re-synchronisation nécessaire -->
                <div v-else-if="creditNote.needs_pennylane_resync" class="alert alert-warning mb-3">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Attention</strong>
                    <p class="mb-0 mt-1 small">
                        Les données de cet avoir ont été modifiées depuis la dernière synchronisation.
                        Il est recommandé de mettre à jour les données.
                    </p>
                </div>

                <!-- Messages de résultat -->
                <div v-if="result" class="mb-3">
                    <div v-if="result.success" class="alert alert-success">
                        <i class="fas fa-check-circle mr-2"></i>
                        {{ result.message }}
                    </div>
                    <div v-else class="alert alert-danger">
                        <i class="fas fa-exclamation-circle mr-2"></i>
                        {{ result.error }}
                    </div>
                </div>

                <!-- Boutons d'action -->
               
                <div class="sync-actions mb-3">
                <button 
                    v-show="!creditNote.is_pennylane_finalized"
                    class="btn btn-primary btn-block"
                    @click="syncCreditNote"
                    :disabled="syncing || finalizing || creditNote.is_pennylane_finalized"
                >
                    
                    <i class="fas" :class="syncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                     Synchroniser
                </button>
                
              <!--
                <button 
                    v-if="creditNote.pennylane_id && !creditNote.is_pennylane_finalized"
                    class="btn btn-success btn-block mt-2"
                    @click="showFinalizeModal = true"
                    :disabled="syncing || finalizing || linking"
                >
                    <i class="fas" :class="finalizing ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
                    E-transmettre
                </button>-->
            </div>

            <!-- Modal de confirmation de finalisation -->
            <div class="modal fade" :class="{ 'show d-block': showFinalizeModal }" tabindex="-1" role="dialog" v-if="showFinalizeModal">
                <div class="modal-dialog modal-dialog-centered" role="document">
                    <div class="modal-content">
                        <div class="modal-header bg-warning text-white">
                            <h5 class="modal-title">
                                <i class="fas fa-exclamation-triangle mr-2"></i>
                                Confirmation d'e-transmission 
                            </h5>
                            <button type="button" class="close text-white" @click="showFinalizeModal = false">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div class="modal-body">
                            <div class="alert alert-warning mb-3">
                                <i class="fas fa-info-circle mr-2"></i>
                                <strong>Action irréversible</strong>
                            </div>
                            <p class="mb-3">
                                Vous êtes sur le point de <strong>e-transmettre cet avoir dans PennyLane</strong>.
                            </p>
                            <div class="bg-light p-3 rounded mb-3">
                                <h6 class="font-weight-bold mb-2">
                                    <i class="fas fa-lock mr-2 text-danger"></i>
                                    Conséquences :
                                </h6>
                                <ul class="mb-0 pl-4">
                                    <li class="mb-2">L'avoir ne pourra <strong>plus être modifié</strong> dans PennyLane</li>
                                    <li class="mb-2">Les modifications locales ne seront <strong>plus synchronisées</strong></li>
                                    <li>L'avoir sera considéré comme <strong>définitif</strong></li>
                                </ul>
                            </div>
                            <p class="text-muted small mb-0">
                                <i class="fas fa-question-circle mr-1"></i>
                                Assurez-vous que toutes les informations de l'avoir sont correctes avant de continuer.
                            </p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" @click="showFinalizeModal = false" :disabled="finalizing">
                                <i class="fas fa-times mr-1"></i>
                                Annuler
                            </button>
                            <button type="button" class="btn btn-success" @click="confirmFinalize" :disabled="finalizing">
                                <i class="fas mr-1" :class="finalizing ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
                                {{ finalizing ? 'Finalisation en cours...' : 'Confirmer la finalisation' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-backdrop fade" :class="{ 'show': showFinalizeModal }" v-if="showFinalizeModal"></div>

            <!-- Historique des logs -->
            <div v-if="logs.length > 0" class="sync-logs mt-4">
                <hr class="my-4">
                <h6 class="font-weight-bold mb-3">
                    <i class="fas fa-history mr-2"></i>
                    Historique de synchronisation
                </h6>
                <div class="list-group">
                    <div v-for="log in logs" :key="log.id" class="list-group-item">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                    <i class="fas mr-2" :class="log.status === 'success' ? 'fa-check-circle text-success' : 'fa-exclamation-circle text-danger'"></i>
                                    <strong>{{ getActionLabel(log.action) }}</strong>
                                    <span class="badge ml-2" :class="log.status === 'success' ? 'badge-success' : 'badge-danger'">
                                        {{ log.status === 'success' ? 'Succès' : 'Erreur' }}
                                    </span>
                                </div>
                                <p class="mb-1 small text-muted">{{ log.message }}</p>
                                <small class="text-muted">
                                    <i class="fas fa-clock mr-1"></i>
                                    {{ formatLogDate(log.created_at) }}
                                </small>

                                <!-- Détails des modifications -->
                                <div v-if="log.details && log.status === 'success'" class="mt-2 small">
                                    <!-- Champs modifiés -->
                                    <div v-if="log.details.modified_fields && log.details.modified_fields.length > 0" class="mb-2">
                                        <span class="text-info">
                                            <i class="fas fa-info-circle mr-1"></i>
                                            {{ log.details.modified_fields.join(', ') }}
                                        </span>
                                    </div>

                                    <!-- Détails des lignes -->
                                    <div v-if="log.details.lines" class="mt-2 p-2 bg-light rounded">
                                        <strong class="d-block mb-2"><i class="fas fa-list mr-1"></i>Détails des lignes :</strong>

                                        <!-- Lignes ajoutées -->
                                        <div v-if="log.details.lines.added && log.details.lines.added.length > 0" class="mb-2">
                                            <span class="badge badge-success">+{{ log.details.lines.added.length }} ajoutée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.added" :key="'added-' + index">
                                                    {{ line.designation }} (Qté: {{ line.quantity }}, {{ formatAmount(line.amount) }}€)
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- Lignes modifiées -->
                                        <div v-if="log.details.lines.modified && log.details.lines.modified.length > 0" class="mb-2">
                                            <span class="badge badge-warning">~{{ log.details.lines.modified.length }} modifiée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.modified" :key="'modified-' + index">
                                                    <strong>{{ line.designation }}</strong>
                                                    <ul class="mb-0 pl-3">
                                                        <li v-for="(change, field) in line.changes" :key="field" class="text-muted">
                                                            {{ ucfirst(field) }}: 
                                                            <span class="text-danger">{{ formatValue(change.old) }}</span>
                                                            → 
                                                            <span class="text-success">{{ formatValue(change.new) }}</span>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- Lignes supprimées -->
                                        <div v-if="log.details.lines.deleted && log.details.lines.deleted.length > 0" class="mb-0">
                                            <span class="badge badge-danger">-{{ log.details.lines.deleted.length }} supprimée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.deleted" :key="'deleted-' + index">
                                                    {{ line.designation }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Utilisateur -->
                            <div v-if="log.user" class="text-right ml-3">
                                <small class="text-muted">{{ log.user.fullname }}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'CreditNotePennylaneSync',
    props: {
        creditNoteId: {
            type: Number,
            required: true
        },
        syncRoute: {
            type: String,
            required: true
        },
        logsRoute: {
            type: String,
            required: true
        },
        initialCreditNote: {
            type: Object,
            default: () => ({})
        },
        initialLogs: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            creditNote: { ...this.initialCreditNote },
            logs: [...this.initialLogs],
            syncing: false,
            finalizing: false,
            linking: false,
            showFinalizeModal: false,
            result: null
        };
    },
    computed: {
        canBeSynced() {
            return this.creditNote.validated_at !== null;
        },
        canLinkToInvoice() {
            // Afficher le bouton si :
            // - L'avoir est synchronisé avec PennyLane
            // - L'avoir est lié à une facture en local (invoice_id existe)
            // - L'avoir n'est pas déjà lié dans PennyLane
            return this.creditNote.pennylane_id && 
                   this.creditNote.invoice_id && 
                   !this.creditNote.is_pennylane_linked;
        }
    },
    methods: {
        syncCreditNote() {
            this.syncing = true;
            this.result = null;

            fetch(`/api/credit-notes/${this.creditNote.id}/pennylane/sync`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = data;

                        if (data.success) {
                            this.creditNote.pennylane_id = data.pennylane_id;
                            this.creditNote.pennylane_synced_at = data.pennylane_synced_at;
                            this.creditNote.needs_pennylane_resync = false;

                            this.refreshLogs();
                            
                            setTimeout(() => {
                                window.location.reload();
                            }, 2000);
                        }
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la synchronisation'
                        };
                    });
                }
                this.syncing = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error('Erreur réseau lors de la synchronisation', {
                    duration: 5000
                });
                this.syncing = false;
            });
        },

        refreshLogs() {
            fetch(this.logsRoute, {
                ...{
                    method: "GET"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.logs = data;
                    });
                }
            }).catch((error) => {
                console.error('Erreur lors du chargement des logs', error);
            });
        },

        getStatusIcon(status) {
            const icons = {
                1: 'fa-file-alt text-secondary',
                2: 'fa-check text-primary',
                3: 'fa-money-bill-wave text-success'
            };
            return icons[status] || 'fa-question-circle text-muted';
        },

        getStatusLabel(status) {
            const labels = {
                1: 'Brouillon',
                2: 'Finalisé',
                3: 'Encaissé'
            };
            return labels[status] || 'Inconnu';
        },

        getActionLabel(action) {
            const labels = {
                'create': 'Création',
                'update': 'Mise à jour',
                'finalize': 'Finalisation',
                'mark_paid': 'Encaissement',
                'link': 'Liaison à la facture'
            };
            return labels[action] || action;
        },

        formatSyncDate(date) {
            return moment(date).format('DD/MM/YYYY à HH:mm');
        },

        formatLogDate(date) {
            return moment(date).format('DD/MM/YYYY à HH:mm');
        },

        formatAmount(amount) {
            return parseFloat(amount).toLocaleString('fr-FR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        formatValue(value) {
            if (typeof value === 'number') {
                return this.formatAmount(value);
            }
            return value;
        },

        ucfirst(string) {
            if (!string) return '';
            return string.charAt(0).toUpperCase() + string.slice(1);
        },

        confirmFinalize() {
            this.finalizing = true;
            this.result = null;
            this.showFinalizeModal = false;

            fetch(`/api/credit-notes/${this.creditNote.id}/pennylane/finalize`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = {
                            success: true,
                            message: 'Avoir finalisé avec succès dans PennyLane'
                        };

                        this.creditNote.pennylane_status = data.pennylane_status;
                        this.creditNote.is_pennylane_finalized = true;

                        this.refreshLogs();

                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la finalisation'
                        };
                    });
                }
                this.finalizing = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error('Erreur réseau lors de la finalisation', {
                    duration: 5000
                });
                this.finalizing = false;
            });
        },

        linkToInvoice() {
            this.linking = true;
            this.result = null;

            fetch(`/api/credit-notes/${this.creditNote.id}/pennylane/link`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = {
                            success: true,
                            message: 'Avoir lié à la facture avec succès dans PennyLane'
                        };

                        this.creditNote.is_pennylane_linked = data.is_pennylane_linked;

                        this.refreshLogs();

                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la liaison'
                        };
                    });
                }
                this.linking = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error('Erreur réseau lors de la liaison', {
                    duration: 5000
                });
                this.linking = false;
            });
        }
    }
};
</script>

<style scoped>
.sync-status {
    border-left: 4px solid #28a745;
}

.status-item {
    display: flex;
    align-items: center;
}

.modal.show {
    background-color: rgba(0, 0, 0, 0.5);
}

.sync-logs .list-group-item {
    border-left: 3px solid transparent;
}

.sync-logs .list-group-item:has(.text-success) {
    border-left-color: #28a745;
}

.sync-logs .list-group-item:has(.text-danger) {
    border-left-color: #dc3545;
}
</style>
